import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { FirebaseError } from 'firebase/app';
import { signInAnonymously } from 'firebase/auth';
import type { JSX } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';

import GrowlerPricingBreakdown from 'components/GrowlerPricingBreakdown';
import { useGrowlersCartPricing } from 'hooks/useGrowlersCartPricing';
import { useGrowlersCashback } from 'hooks/useGrowlersCashback';
import { useGrowlerCart } from 'services/cart/GrowlerCartContext';
import { auth } from 'services/firebase/client';
import { saveGrowlerCustomer } from 'services/firebase/growlerCustomerService';
import { roundCurrency } from 'utils/currency';
import {
  formatGrowlerBirthDate,
  formatGrowlerCep,
  GROWLER_DELIVERY_TIME,
  GROWLER_SERVICE_AREA_MESSAGE,
  GROWLER_UNDERAGE_MESSAGE,
  isGrowlerServiceArea,
  isGrowlerUnderage,
} from 'utils/growlersFlow';
import {
  formatPhoneMask,
  sanitizePhone,
  validatePhone,
} from 'utils/phoneValidation';
import {
  GROWLER_CUSTOMER_STORAGE_KEY,
  GROWLER_ORDER_STORAGE_KEY,
} from 'utils/types/GrowlerCustomer';
import { validateCPF } from 'utils/validation';
import styles from 'AppRoutes/OrderConfirmation/styles';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GrowlerOrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart } = useGrowlerCart();
  const pricing = useGrowlersCartPricing(cart.totalItems, cart.totalValue);
  const merchandiseSubtotal = roundCurrency(
    Math.max(0, pricing.subtotal - pricing.discount),
  );
  const cashback = useGrowlersCashback(merchandiseSubtotal);
  const [fullName, setFullName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [uf, setUf] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [serviceAreaError, setServiceAreaError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const lastFetchedCep = useRef('');
  const fetchIdRef = useRef(0);

  const clearResolvedAddress = useCallback(() => {
    setStreet('');
    setNeighborhood('');
    setCity('');
    setUf('');
    setServiceAreaError(null);
  }, []);

  const handleFetchCep = useCallback(
    async (rawCep = cep) => {
      const cepDigits = rawCep.replace(/\D/g, '');
      if (cepDigits.length !== 8) {
        setErrors(prev => ({
          ...prev,
          cep: 'Digite um CEP válido (8 dígitos).',
        }));
        clearResolvedAddress();
        return;
      }

      if (lastFetchedCep.current === cepDigits) {
        return;
      }

      const fetchId = ++fetchIdRef.current;
      lastFetchedCep.current = cepDigits;
      setCepLoading(true);
      setServiceAreaError(null);
      setErrors(prev => ({ ...prev, cep: '' }));

      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${cepDigits}/json/`,
        );
        if (fetchId !== fetchIdRef.current) return;
        if (response.data.erro || !response.data.localidade) {
          lastFetchedCep.current = '';
          setErrors(prev => ({ ...prev, cep: 'CEP não encontrado.' }));
          clearResolvedAddress();
          return;
        }

        const nextCity = response.data.localidade || '';
        const nextUf = response.data.uf || '';
        setCep(formatGrowlerCep(cepDigits));
        setStreet(response.data.logradouro || '');
        setNeighborhood(response.data.bairro || '');
        setCity(nextCity);
        setUf(nextUf);

        if (!isGrowlerServiceArea(nextCity, nextUf)) {
          setServiceAreaError(GROWLER_SERVICE_AREA_MESSAGE);
        }
      } catch {
        if (fetchId !== fetchIdRef.current) return;
        lastFetchedCep.current = '';
        setErrors(prev => ({
          ...prev,
          cep: 'Erro ao buscar o CEP. Tente novamente.',
        }));
        clearResolvedAddress();
      } finally {
        if (fetchId === fetchIdRef.current) {
          setCepLoading(false);
        }
      }
    },
    [cep, clearResolvedAddress],
  );

  const handleCepChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const previousDigits = cep.replace(/\D/g, '');
    setCep(value);
    if (digits === previousDigits) return;

    lastFetchedCep.current = '';
    fetchIdRef.current += 1;
    clearResolvedAddress();
    setErrors(prev => ({ ...prev, cep: '' }));
    if (digits.length === 8) {
      void handleFetchCep(value);
    }
  };

  const hasResolvedAddress = Boolean(city && uf);
  const isInServiceArea = isGrowlerServiceArea(city, uf);
  const isUnderage = isGrowlerUnderage(birthDate);

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};
    if (!fullName.trim() || fullName.trim().split(' ').length < 2) {
      nextErrors.fullName = 'Informe o nome completo';
    }
    if (!validatePhone(whatsapp)) {
      nextErrors.whatsapp = 'Informe um WhatsApp válido';
    }
    if (!emailRegex.test(email.trim())) {
      nextErrors.email = 'Informe um e-mail válido';
    }
    if (!validateCPF(cpf)) {
      nextErrors.cpf = 'Informe um CPF válido';
    }
    if (!birthDate || !birthDate.isValid()) {
      nextErrors.birthDate = 'Informe a data de nascimento';
    } else if (birthDate.isAfter(dayjs(), 'day')) {
      nextErrors.birthDate = 'Informe uma data de nascimento válida';
    }
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      nextErrors.cep = 'Digite um CEP válido (8 dígitos).';
    } else if (!hasResolvedAddress) {
      nextErrors.cep = 'Busque um CEP válido antes de continuar.';
    }
    if (hasResolvedAddress && !isInServiceArea) {
      setServiceAreaError(GROWLER_SERVICE_AREA_MESSAGE);
    }
    if (isInServiceArea && !houseNumber.trim()) {
      nextErrors.houseNumber = 'Informe o número';
    }
    setErrors(nextErrors);
    return (
      Object.keys(nextErrors).length === 0 &&
      hasResolvedAddress &&
      isInServiceArea &&
      !isUnderage
    );
  };

  const handleProceed = async () => {
    if (cart.items.length === 0 || !validateForm() || !birthDate) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const formattedCep = formatGrowlerCep(cep);
      const condominium =
        [street, neighborhood].filter(Boolean).join(' — ') || `${city} - ${uf}`;
      const customer = {
        fullName: fullName.trim(),
        whatsapp: sanitizePhone(whatsapp),
        email: email.trim().toLowerCase(),
        cpf: cpf.replace(/\D/g, ''),
        birthDate: formatGrowlerBirthDate(birthDate),
        condominium,
        houseNumber: houseNumber.trim(),
        address: {
          cep: formattedCep,
          street,
          neighborhood,
          city,
          uf,
          number: houseNumber.trim(),
          complement: complement.trim(),
        },
        uid: auth.currentUser?.uid,
      };

      const customerId = await saveGrowlerCustomer(customer);
      const payload = { ...customer, id: customerId };

      localStorage.setItem(
        GROWLER_CUSTOMER_STORAGE_KEY,
        JSON.stringify(payload),
      );
      localStorage.setItem(
        GROWLER_ORDER_STORAGE_KEY,
        JSON.stringify({
          customer: payload,
          shipping: {
            id: 'entrega',
            price: pricing.shippingCost,
            description:
              pricing.shippingCost === 0 ? 'Entrega grátis' : 'Taxa de entrega',
            deliveryTime: GROWLER_DELIVERY_TIME,
          },
        }),
      );

      navigate('/growlers/shipping');
    } catch (error) {
      console.error(error);
      const code = error instanceof FirebaseError ? error.code : '';
      if (code === 'auth/admin-restricted-operation') {
        setSubmitError(
          'O login anônimo está desativado neste projeto Firebase. Ative Authentication → Sign-in method → Anonymous.',
        );
      } else if (code === 'permission-denied') {
        setSubmitError(
          'Sem permissão para salvar os dados. Confira as regras do Firestore para growlerCustomers.',
        );
      } else {
        setSubmitError('Não foi possível salvar seus dados. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography variant="h5" sx={styles.emptyMessage}>
          Seu carrinho de growlers está vazio
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/growlers')}
          sx={styles.shopButton}
        >
          Ver growlers
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Typography variant="h4" sx={styles.title}>
        CONFIRMAR PEDIDO
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={6}>
          <List sx={styles.itemsList}>
            {cart.items.map(item => (
              <Card key={item.id} sx={styles.itemCard}>
                <ListItem sx={styles.listItem}>
                  <ListItemAvatar>
                    <CardMedia
                      component="img"
                      image={item.growler.image}
                      alt={item.growler.name}
                      sx={styles.beerImage}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="h6" sx={styles.beerName}>
                        {item.growler.name.toUpperCase()}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2">
                        R$ {item.growler.price.toFixed(2).replace('.', ',')}
                      </Typography>
                    }
                  />
                  <Box sx={styles.itemActions}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={styles.excludeButton}
                      onClick={() => removeFromCart(item.growler.id)}
                    >
                      EXCLUIR
                    </Button>
                    <Box sx={styles.quantityControls}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={item.quantity <= 1}
                        onClick={() =>
                          updateQuantity(item.growler.id, item.quantity - 1)
                        }
                      >
                        -
                      </Button>
                      <Typography sx={styles.quantity}>
                        {item.quantity}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          updateQuantity(item.growler.id, item.quantity + 1)
                        }
                      >
                        +
                      </Button>
                    </Box>
                  </Box>
                </ListItem>
              </Card>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={styles.summaryCard}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Seus dados para entrega
              </Typography>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              )}
              <TextField
                fullWidth
                label="Nome completo"
                value={fullName}
                onChange={event => setFullName(event.target.value)}
                error={!!errors.fullName}
                helperText={errors.fullName}
                sx={{ mb: 2 }}
              />
              <InputMask
                mask="(99) 99999-9999"
                value={whatsapp}
                onChange={event =>
                  setWhatsapp(formatPhoneMask(event.target.value))
                }
                maskChar={null}
              >
                {(
                  inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                ): JSX.Element => {
                  const { size: _size, ...rest } = inputProps;
                  return (
                    <TextField
                      {...rest}
                      fullWidth
                      label="WhatsApp"
                      error={!!errors.whatsapp}
                      helperText={
                        errors.whatsapp ||
                        'Enviaremos avisos sobre o pedido e a entrega'
                      }
                      variant="outlined"
                      color="primary"
                      sx={{ mb: 2 }}
                    />
                  );
                }}
              </InputMask>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ mb: 2 }}
              />
              <InputMask
                mask="999.999.999-99"
                value={cpf}
                onChange={event => {
                  setCpf(event.target.value);
                  setErrors(prev => ({ ...prev, cpf: '' }));
                }}
                maskChar={null}
              >
                {(
                  inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                ): JSX.Element => {
                  const { size: _size, color: _color, ...rest } = inputProps;
                  return (
                    <TextField
                      {...rest}
                      fullWidth
                      label="CPF"
                      error={!!errors.cpf}
                      helperText={errors.cpf}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ mb: 2 }}
                    />
                  );
                }}
              </InputMask>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <DatePicker
                  label="Data de nascimento"
                  value={birthDate}
                  onChange={value => {
                    setBirthDate(value);
                    setErrors(prev => ({ ...prev, birthDate: '' }));
                  }}
                  format="DD/MM/YYYY"
                  minDate={dayjs().subtract(120, 'year')}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.birthDate,
                      helperText: errors.birthDate,
                      sx: { mb: 2 },
                    },
                  }}
                />
              </LocalizationProvider>
              {isUnderage && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {GROWLER_UNDERAGE_MESSAGE}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={8}>
                  <InputMask
                    mask="99999-999"
                    value={cep}
                    onChange={event => handleCepChange(event.target.value)}
                    onBlur={() => {
                      void handleFetchCep();
                    }}
                    maskChar={null}
                  >
                    {(
                      inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                    ): JSX.Element => {
                      const {
                        size: _size,
                        color: _color,
                        ...rest
                      } = inputProps;
                      return (
                        <TextField
                          {...rest}
                          fullWidth
                          label="CEP"
                          error={!!errors.cep}
                          helperText={errors.cep}
                          InputProps={{
                            endAdornment: cepLoading ? (
                              <CircularProgress size={20} />
                            ) : undefined,
                          }}
                        />
                      );
                    }}
                  </InputMask>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ height: '56px' }}
                    onClick={() => {
                      lastFetchedCep.current = '';
                      void handleFetchCep();
                    }}
                    disabled={cepLoading}
                  >
                    Buscar
                  </Button>
                </Grid>
              </Grid>
              {hasResolvedAddress && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  {street && <Typography variant="body1">{street}</Typography>}
                  {neighborhood && (
                    <Typography variant="body2" color="text.secondary">
                      {neighborhood}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {city} - {uf}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CEP {formatGrowlerCep(cep)}
                  </Typography>
                </Box>
              )}
              {serviceAreaError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {serviceAreaError}
                </Alert>
              )}
              {hasResolvedAddress && isInServiceArea && (
                <>
                  <TextField
                    fullWidth
                    label="Número"
                    value={houseNumber}
                    onChange={event => setHouseNumber(event.target.value)}
                    error={!!errors.houseNumber}
                    helperText={errors.houseNumber}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Complemento"
                    value={complement}
                    onChange={event => setComplement(event.target.value)}
                    helperText="Apto, bloco, casa, lote, etc."
                    sx={{ mb: 3 }}
                  />
                </>
              )}
              <GrowlerPricingBreakdown
                pricing={pricing}
                cashbackEarnAmount={cashback.earnAmount}
                cashbackEarnPercent={cashback.earnPercent}
                rowSx={styles.priceRow}
                hintSx={{ display: 'block', mb: 1, color: 'text.secondary' }}
              />
              <Box sx={styles.actionButtons}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/growlers')}
                  sx={styles.continueShoppingButton}
                >
                  Comprar mais
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleProceed}
                  disabled={
                    isSubmitting ||
                    pricing.loading ||
                    cepLoading ||
                    !!serviceAreaError ||
                    isUnderage
                  }
                  sx={styles.proceedButton}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    'AVANÇAR'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GrowlerOrderConfirmation;
